/**
 * Auth actions + the compatibility surface the rest of the shell imports.
 *
 * The STATE lives in `./authState.ts` — one `HolderPhase`, one writer, one
 * reducer (the SDK's). This module holds the two things that are not state:
 * the sign-out action (which owns the teardown registry) and the credential-
 * injection adapter the E2E helper drives. Everything else here is a re-export
 * of an `authState` projection so the ~50 existing `stores/auth/atoms` imports
 * keep resolving.
 *
 * There is no longer a writable `tokenAtom` / `claimsAtom` / `stytchSession*`.
 * A token can only enter the shell as a `CredentialAcquired` /
 * `CredentialRenewed` event, which is what makes "a token with no claims" and
 * "claims with no token" unrepresentable rather than merely discouraged.
 */

import { atom } from 'jotai';
import { configureAtomDevtools } from '../utils/tracking';
import { getSDK } from '../../services/sdk-instance';
import {
  applyAuthEventAction,
  authMaterialAtom,
  credentialAcquiredEvent,
  delegatedMaterial,
  isDelegatedSessionToken,
} from './authState';
import { resetAccountScopedStores } from './teardown';
import type { SignedOutReason } from '@localagent/sdk';

export {
  applyAuthEventAction,
  authDegradedReasonAtom,
  authIdentityAtom,
  authMaterialAtom,
  authPhaseAtom,
  authPhaseNameAtom,
  claimsAtom,
  credentialAcquiredEvent,
  DELEGATED_SESSION_TOKEN,
  delegatedMaterial,
  expiresAtFromSessionJwt,
  identityFromSessionJwt,
  holdsIdentityAtom,
  isDelegatedSessionToken,
  stytchSessionJwtAtom,
  stytchSessionTokenAtom,
  tokenAtom,
  verdictEvent,
} from './authState';
export type { AuthClaims } from './authState';

/**
 * Inject acquired credential material.
 *
 * This is the E2E / test-helper entry point (`window.__E2E_TEST_HELPERS__
 * .setAuthToken`), and the only remaining caller that hands the shell a raw
 * token rather than an event. It is still a LOCAL MATERIAL EVENT under the
 * derivation rule — "this holder acquired this credential" — and it classifies
 * activated vs claim-less through the same `credentialAcquiredEvent` helper
 * every production acquisition path uses, so an injected session lands in
 * exactly the phase a real one would.
 *
 * Production code does NOT call this: `SessionActivationGate`,
 * `DaemonSessionGate`, `SessionRenewalDriver` and `switchOrganizationAction`
 * each dispatch the specific event their situation actually is.
 *
 * There is no provider invalidation beside the dispatch any more, and
 * that is the point: this action used to be the ONLY path that invalidated, so
 * the four production paths above — which never call it — silently did not.
 * `applyAuthEventAction` now drops the cached bearer itself whenever a
 * transition supersedes it, awaited, for every caller (app-shell ADR-001 §8).
 */
export const setSessionAction = configureAtomDevtools(
  atom<null, [{ token: string; sessionToken?: string | null }], Promise<void>>(
    null,
    async (_get, set, payload) => {
      await set(
        applyAuthEventAction,
        credentialAcquiredEvent(
          // The opaque Stytch token is the RENEWAL key. A caller that has none
          // says so with the delegated sentinel rather than passing the JWT (a
          // loud lie) or an empty string (a silent one).
          payload.sessionToken
            ? { session_jwt: payload.token, session_token: payload.sessionToken }
            : delegatedMaterial(payload.token),
        ),
      );
    },
  ),
  'auth/setSession',
);

/**
 * THE sign-out.
 *
 * Three steps, in this order:
 *   1. `LocalClear` through the SDK reducer — the only legal LOCAL route to
 *      `no_credential`. Doing this first means no derived projection can report
 *      a tenancy while the stores below are being emptied. Dropping the
 *      identity supersedes the cached bearer, so the writer invalidates the auth
 *      provider as part of this step.
 *   2. Every registered account-scoped store, via the teardown registry. This is
 *      what reclaims the react-query cache and every other account store —
 *      which previously survived a sign-out into the next account's shell. The
 *      bearer has no cache to drop again afterwards: it is the phase's own
 *      material, projected (sdk ADR-010).
 */
export const clearAuthAction = configureAtomDevtools(
  atom<null, [SignedOutReason?], Promise<void>>(null, async (get, set, reason) => {
    await set(applyAuthEventAction, {
      type: 'LocalClear',
      reason: reason ?? 'user_signout',
    });
    await resetAccountScopedStores(get, set);
  }),
  'auth/clearAuth',
);

/**
 * Switch the active org via Stytch sessions.Exchange + re-activate.
 *
 * `hydrate` runs the mandatory two-step Stytch client hydration
 * (`stytch.session.updateSession` + `stytch.session.authenticate` — account
 * ADR-021) AFTER the phase moves, so a hung authenticate cannot pin the
 * picker on the previous org. The atom has no Stytch client instance (that
 * is a React-hook-scoped value), so the caller — which does — supplies it.
 *
 * The resulting event is `CredentialAcquired`, which the reducer accepts from
 * EVERY phase precisely because switching organizations over a live session is
 * a legitimate re-acquisition, not a downgrade.
 */
export const switchOrganizationAction = configureAtomDevtools(
  atom<
    null,
    [string, (tokens: { session_token: string; session_jwt: string }) => Promise<void>],
    Promise<void>
  >(null, async (get, set, organizationId, hydrate) => {
    const material = get(authMaterialAtom);
    const sessionToken =
      material === null || isDelegatedSessionToken(material.session_token)
        ? undefined
        : material.session_token;
    const res = await getSDK().auth.switchOrganization({
      organization_id: organizationId,
      session_token: sessionToken,
    });
    // Identity moves on the backend pair, not on Stytch JS authenticate.
    // Hydrate after so a hung `session.authenticate()` cannot leave the
    // picker disabled on the previous org (app-shell ADR-001).
    await set(
      applyAuthEventAction,
      credentialAcquiredEvent({
        session_jwt: res.session_jwt,
        session_token: res.session_token,
      }),
    );
    await hydrate({ session_token: res.session_token, session_jwt: res.session_jwt });
  }),
  'auth/switchOrganization',
);
